import { useState, FormEvent } from "react";

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;

    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d77vnn2dqn9mib2tqf9g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-[#0a0a0a] py-16 sm:py-20 md:py-28 px-5 sm:px-8 md:px-16">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 lg:gap-24">
        {/* Left info */}
        <div className="flex flex-col justify-center">
          <h2 className="text-white font-serif text-3xl sm:text-4xl md:text-5xl leading-tight mb-5 sm:mb-8">
            Let&apos;s Discuss<br />Your Next<br />Opportunity.
          </h2>
          <p className="text-white/50 text-sm leading-relaxed mb-7 sm:mb-12 max-w-md">
            We engage exclusively with qualified investors, family offices, and institutional capital partners. All discussions are strictly confidential.
          </p>

          <div className="space-y-4 sm:space-y-6">
            {[
              { label: "HEADQUARTERS", value: "Berkeley House, Berkeley Square, Mayfair, London" },
              { label: "TELEPHONE", value: "+44 7880 888090" },
              { label: "EMAIL", value: "enquiries@88elevencapital.com" },
            ].map((item) => (
              <div key={item.label} className="border-l border-[#c9a84c]/40 pl-4 sm:pl-5">
                <p className="text-[#c9a84c] text-[10px] tracking-[3px] uppercase mb-1">{item.label}</p>
                <p className="text-white/60 text-sm break-words">{item.value}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right form */}
        <div>
          {submitted ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-14 sm:py-20 border border-white/10">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center border border-[#c9a84c] mb-5 sm:mb-6">
                <i className="ri-check-line text-[#c9a84c] text-xl sm:text-2xl" />
              </div>
              <h3 className="text-white font-serif text-xl sm:text-2xl mb-3">Enquiry Received</h3>
              <p className="text-white/50 text-sm max-w-xs leading-relaxed">
                Thank you for reaching out. A member of our team will be in contact within 24 hours.
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              id="contact-form"
              onSubmit={handleSubmit}
              className="space-y-4 sm:space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[3px] text-white/40 uppercase mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    required
                    className="w-full bg-transparent border border-white/15 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 transition-colors placeholder-white/20"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[3px] text-white/40 uppercase mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full bg-transparent border border-white/15 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 transition-colors placeholder-white/20"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] tracking-[3px] text-white/40 uppercase mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    className="w-full bg-transparent border border-white/15 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 transition-colors placeholder-white/20"
                    placeholder="Your Company"
                  />
                </div>
                <div>
                  <label className="block text-[10px] tracking-[3px] text-white/40 uppercase mb-2">Enquiry Type</label>
                  <select
                    name="enquiry_type"
                    className="w-full bg-[#0a0a0a] border border-white/15 text-white/60 text-sm px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 transition-colors cursor-pointer"
                  >
                    <option value="">Select type</option>
                    <option value="M&A Advisory">M&amp;A Advisory</option>
                    <option value="Capital Solutions">Capital Solutions</option>
                    <option value="Portfolio Management">Portfolio Management</option>
                    <option value="Strategic Consulting">Strategic Consulting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] tracking-[3px] text-white/40 uppercase mb-2">
                  Message * <span className="text-white/25 normal-case tracking-normal">({charCount}/500)</span>
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full bg-transparent border border-white/15 text-white text-sm px-4 py-3 focus:outline-none focus:border-[#c9a84c]/60 transition-colors resize-none placeholder-white/20"
                  placeholder="Please describe your enquiry..."
                />
              </div>

              <p className="text-white/25 text-[10px] tracking-[2px] uppercase border-l border-white/15 pl-3">
                Referral enquiries only.
              </p>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#c9a84c] text-black text-xs tracking-[3px] sm:tracking-[4px] uppercase hover:bg-[#b8973b] transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? "SENDING..." : "SUBMIT ENQUIRY"}
              </button>

              <p className="text-white/25 text-[10px] tracking-[1px] text-center">
                All communications are strictly confidential and protected by NDA.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
